/**
 * Integrations → Printers (z8nrz7evkc): the pure rules behind the lists and
 * the Printer association table.
 * The screens import everything here, so the checks a user sees are the ones
 * these tests cover. The server checks the same rules again; the messages
 * below are the ticket's own wording.
 *
 * Two kinds of label design (template) and printer:
 *  - ISBT: the product bag label. HemaTrax with NiceLabel owns the layout, so a
 *    template is only a `.lbl` file name, and a printer is its queue name on the
 *    print computer.
 *  - Zebra: supply, equipment, reagent and kit labels. Smart CGT owns the layout:
 *    each change is a new version, saved as a draft and used only once it is
 *    approved. A printer is a network address.
 */

export const ISBT_USES = ['full', 'partial', 'cryo', 'din_set', 'manufacturing', 'cord_blood_set'] as const
export type IsbtUse = (typeof ISBT_USES)[number]

export const ISBT_DATE_FORMATS = ['standard', 'mfg'] as const
export type IsbtDateFormat = (typeof ISBT_DATE_FORMATS)[number]

export const ZEBRA_CONNECTIONS = ['direct', 'print_server'] as const
export type ZebraConnection = (typeof ZEBRA_CONNECTIONS)[number]

export const USE_LABEL: Record<IsbtUse, string> = {
  full: 'Full',
  partial: 'Partial',
  cryo: 'Cryo',
  din_set: 'DIN set',
  manufacturing: 'Manufacturing',
  cord_blood_set: 'Cord blood set',
}

export const DATE_FORMAT_LABEL: Record<IsbtDateFormat, string> = {
  standard: 'Standard',
  mfg: 'MFG',
}

export const CONNECTION_LABEL: Record<ZebraConnection, string> = {
  direct: 'Direct to printer',
  print_server: 'Print server',
}

/** A null ISBT label size: the seeded DIN Set and Cord Blood Set have none yet. */
export const SIZE_TO_CONFIRM = 'To confirm'

export const QUEUE_NAME_MAX = 35
export const DEFAULT_ZEBRA_PORT = 9100

export const MESSAGES = {
  displayName: 'Enter a display name.',
  fileName: 'Use letters, digits, spaces and . _ - only, ending in .lbl (max 255 characters).',
  queueRequired: 'Enter the queue name.',
  queueTooLong: "Max 35 characters: the job file's line 3 holds 35.",
  queueControl: 'No line breaks or control characters.',
  pickTemplate: 'Pick at least one template.',
  host: 'Enter the printer or print server host.',
  port: 'Port must be 1–65535.',
  serverQueue: 'Enter the queue name on the print server.',
  testPrintSent: 'Test label sent. 202 means it was handed off, not that it printed: check the label.',
  testPrintUnavailable: 'The print relay is unavailable. Nothing was sent.',
  templateName: 'Enter a template name.',
  effectiveFrom: 'Pick the date it takes effect.',
  reasonNew: 'Say why this template is needed.',
  reasonEdit: 'Say what changed and why.',
  zplFraming: 'A ZPL job must start with ^XA and end with ^XZ.',
  layoutTooLong: 'The layout can be at most 20000 characters.',
} as const

// ─── Field checks ─────────────────────────────────────────────────────────────

// 1 to 251 allowed characters plus `.lbl`: 255 at most in all.
const FILE_NAME = /^[A-Za-z0-9._ -]{1,251}\.lbl$/
// \x00-\x1F covers CR and LF; \x7F is DEL.
const CONTROL_CHARS = /[\x00-\x1F\x7F]/

export function fileNameError(fileName: string): string | null {
  return FILE_NAME.test(fileName) ? null : MESSAGES.fileName
}

// The queue name checks count the trimmed name, because the server trims it
// before it checks and stores it.

/** The live counter under the queue name field, e.g. `12 / 35`. */
export function queueNameCount(queueName: string): string {
  return `${queueName.trim().length} / ${QUEUE_NAME_MAX}`
}

export function queueNameTooLong(queueName: string): boolean {
  return queueName.trim().length > QUEUE_NAME_MAX
}

export function queueNameError(queueName: string): string | null {
  const trimmed = queueName.trim()
  if (trimmed.length === 0) return MESSAGES.queueRequired
  if (trimmed.length > QUEUE_NAME_MAX) return MESSAGES.queueTooLong
  if (CONTROL_CHARS.test(trimmed)) return MESSAGES.queueControl
  return null
}

/** The port as typed, or null when it is not a whole number from 1 to 65535. */
export function parsePort(port: string): number | null {
  const trimmed = port.trim()
  if (!/^\d+$/.test(trimmed)) return null
  const n = Number(trimmed)
  return n >= 1 && n <= 65535 ? n : null
}

export function portError(port: string): string | null {
  return parsePort(port) === null ? MESSAGES.port : null
}

// ─── Status chips and list cells ──────────────────────────────────────────────

export type ChipTone = 'ok' | 'warn' | 'off'

export function isbtTemplateStatus(t: { active: boolean; approvedAt: string | null }): {
  label: string
  tone: ChipTone
} {
  if (!t.active) return { label: 'Retired', tone: 'off' }
  if (t.approvedAt) return { label: 'Approved', tone: 'ok' }
  return { label: 'Awaiting test print', tone: 'warn' }
}

/** Both printer lists: "sign to print" marks a printer that asks for a signature. */
export function printerStatus(p: { active: boolean; signatureRequired: boolean }): {
  label: string
  tone: ChipTone
} {
  if (!p.active) return { label: 'Retired', tone: 'off' }
  return { label: p.signatureRequired ? 'Active · sign to print' : 'Active', tone: 'ok' }
}

export type ZebraTemplateStatus = 'draft' | 'approved' | 'scheduled' | 'retired'

/**
 * `scheduled`: approved, but its first day has not started, and no earlier
 * version is in use. The chip names the day.
 */
export function zebraTemplateStatus(t: {
  status: ZebraTemplateStatus
  nextVersion: { effectiveDate: string | null } | null
}): { label: string; tone: ChipTone } {
  if (t.status === 'retired') return { label: 'Retired', tone: 'off' }
  if (t.status === 'approved') return { label: 'Approved', tone: 'ok' }
  if (t.status === 'scheduled') {
    const day = t.nextVersion?.effectiveDate
    return { label: day ? `Approved from ${dayText(day)}` : 'Approved', tone: 'ok' }
  }
  return { label: 'Draft', tone: 'warn' }
}

export function labelSizeText(size: string | null): string {
  return size ?? SIZE_TO_CONFIRM
}

export function sizeAndResolution(stock: string, resolution: string): string {
  return `${stock} at ${resolution}`
}

export function placeholderCountText(count: number): string {
  return `${count} ${count === 1 ? 'placeholder' : 'placeholders'}`
}

/** A printer's allowed templates as one cell; retired links stay, marked as such. */
export function allowedTemplatesText(templates: { name: string; active: boolean }[]): string {
  return templates.map(t => (t.active ? t.name : `${t.name} (Retired)`)).join(', ')
}

export function zebraConnectionText(p: { connection: ZebraConnection; serverQueueName: string | null }): string {
  return p.connection === 'print_server' ? `Print server · ${p.serverQueueName ?? ''}` : 'Direct'
}

/** A day for display, e.g. `Oct 5, 2026`; null for no date. */
export function formatDay(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// ─── ISBT templates ───────────────────────────────────────────────────────────

interface IsbtTemplateLike {
  id: string
  displayName: string
  fileName: string
  labelSize: string | null
  active: boolean
  approvedAt: string | null
}

/**
 * Approve needs a saved, active template with a size that is not approved yet.
 * A template with no size cannot be approved (requirement 11).
 */
export function canApproveIsbtTemplate(t: { active: boolean; labelSize: string | null; approvedAt: string | null }): boolean {
  return t.active && t.labelSize !== null && t.approvedAt === null
}

/** One checkbox in a printer's "Allowed templates". */
export interface AllowedChoice {
  id: string
  name: string
  /** The `.lbl` file for ISBT; empty for Zebra. */
  detail: string
  /** A link made before the template was retired: shown checked, cannot change. */
  retired: boolean
}

/**
 * What an ISBT printer of `stock` may pick: approved, active templates of that
 * size (requirements 8 and 14). Links to templates retired since stay on the
 * printer, so they are listed too, after the rest, marked retired (requirement 9).
 */
export function isbtAllowedChoices(
  templates: IsbtTemplateLike[],
  stock: string,
  linked: { id: string; displayName: string; fileName: string; labelSize: string | null; active: boolean }[] = [],
): AllowedChoice[] {
  const open = templates
    .filter(t => t.active && t.approvedAt !== null && t.labelSize === stock)
    .map(t => ({ id: t.id, name: t.displayName, detail: t.fileName, retired: false }))
  const retired = linked
    .filter(t => !t.active && t.labelSize === stock)
    .map(t => ({ id: t.id, name: t.displayName, detail: t.fileName, retired: true }))
  return [...open, ...retired]
}

/** Active templates of `stock` that still wait for a test print and approval. */
export function isbtPendingCount(templates: IsbtTemplateLike[], stock: string): number {
  return templates.filter(t => t.active && t.approvedAt === null && t.labelSize === stock).length
}

export function pendingTemplatesNote(count: number): string | null {
  if (count <= 0) return null
  return count === 1
    ? '1 more template of this size is awaiting a test print and approval.'
    : `${count} more templates of this size are awaiting a test print and approval.`
}

/**
 * The test print picker: any active template of the loaded size, approved or
 * not, because a test print is how a template gets approved (requirement 16).
 */
export function isbtTestPrintOptions(templates: IsbtTemplateLike[], stock: string): { id: string; label: string }[] {
  return templates
    .filter(t => t.active && t.labelSize === stock)
    .map(t => ({ id: t.id, label: t.approvedAt ? t.displayName : `${t.displayName} (awaiting approval)` }))
}

/** A test print needs only a name and a valid queue name; the printer need not be saved. */
export function canTestPrint(draft: { displayName: string; queueName: string }): boolean {
  return draft.displayName.trim() !== '' && queueNameError(draft.queueName) === null
}

/** Changing the loaded size drops templates of any other size (requirement 15). */
export function pruneIsbtAllowed(
  ids: string[],
  templates: { id: string; labelSize: string | null }[],
  stock: string,
): string[] {
  const size = new Map(templates.map(t => [t.id, t.labelSize]))
  return ids.filter(id => size.get(id) === stock)
}

/** At least one pick that is not a retired link (those do not count). */
export function hasOpenPick(ids: string[], choices: AllowedChoice[]): boolean {
  const picked = new Set(ids)
  return choices.some(c => !c.retired && picked.has(c.id))
}

// ─── Zebra printers ───────────────────────────────────────────────────────────

interface ZebraFit {
  id: string
  name: string
  labelStock: string
  resolution: string
}

/**
 * What a Zebra printer may pick: templates with an approved version in use,
 * drawn for this stock and resolution. Links to templates retired since stay
 * on the printer, listed after the rest, marked retired.
 */
export function zebraAllowedChoices(
  templates: (ZebraFit & { status: ZebraTemplateStatus })[],
  stock: string,
  resolution: string,
  linked: (ZebraFit & { active: boolean })[] = [],
): AllowedChoice[] {
  const fits = (t: ZebraFit) => t.labelStock === stock && t.resolution === resolution
  const open = templates
    .filter(t => t.status === 'approved' && fits(t))
    .map(t => ({ id: t.id, name: t.name, detail: '', retired: false }))
  const retired = linked
    .filter(t => !t.active && fits(t))
    .map(t => ({ id: t.id, name: t.name, detail: '', retired: true }))
  return [...open, ...retired]
}

/** Changing size or resolution drops templates that no longer match (requirement 26). */
export function pruneZebraAllowed(
  ids: string[],
  templates: { id: string; labelStock: string; resolution: string }[],
  stock: string,
  resolution: string,
): string[] {
  const byId = new Map(templates.map(t => [t.id, t]))
  return ids.filter(id => {
    const t = byId.get(id)
    return t !== undefined && t.labelStock === stock && t.resolution === resolution
  })
}

// ─── Zebra templates ──────────────────────────────────────────────────────────

/** The longest layout the server stores. */
export const ZEBRA_BODY_MAX = 20000

/**
 * The layout a new template starts with: a small supply label that uses only
 * placeholders the backend can fill.
 */
export const STARTER_ZPL = [
  '^XA',
  '^PW788',
  '^LL0300',
  '^FT12,52^A0N,40,40^FH\\^FD{{qa_state}}^FS',
  '^FT12,100^A0N,26,26^FH\\^FD{{part_num}}  {{lot_num}}^FS',
  '^FT12,140^A0N,26,26^FH\\^FDStore at {{stor_spec}}^FS',
  '^BY2^FT12,250^BCN,60,N^FD>:{{supply_bcid}}^FS',
  '^XZ',
].join('\n')

interface ZebraVersionLike {
  id: string
  version: number
  placeholders: string[]
  /** The tenant's calendar day it takes effect, `YYYY-MM-DD`; null: from the start. */
  effectiveDate: string | null
}

interface ZebraTemplateVersions<V extends ZebraVersionLike = ZebraVersionLike> {
  /** The approved version in use now. */
  liveVersion: V | null
  /** An approved version whose first day has not started yet. */
  nextVersion: V | null
  /** The draft waiting for approval. */
  draftVersion: V | null
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * A `YYYY-MM-DD` day as `Oct 20, 2026` (or `Oct 20` without the year). The
 * day is the tenant's own, so it is read as written, with no time zone.
 */
export function dayText(day: string, withYear = true): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  const month = m ? MONTHS[Number(m[2]) - 1] : undefined
  if (!m || !month) return day
  const short = `${month} ${Number(m[3])}`
  return withYear ? `${short}, ${m[1]}` : short
}

/** A version's effective day, or `From the start` when it has none. */
export function effectiveFromText(effectiveDate: string | null): string {
  return effectiveDate ? dayText(effectiveDate) : 'From the start'
}

/** The version the list describes: the one in use, else the next, else the draft. */
export function zebraShownVersion<V extends ZebraVersionLike>(t: ZebraTemplateVersions<V>): V | null {
  return t.liveVersion ?? t.nextVersion ?? t.draftVersion
}

/** The newest version: a change starts from it. */
export function zebraLatestVersion<V extends ZebraVersionLike>(t: ZebraTemplateVersions<V>): V | null {
  return t.draftVersion ?? t.nextVersion ?? t.liveVersion
}

/**
 * A row in a template's version history: the one in use, the one approved to
 * start later, a draft, or an earlier approved one.
 */
export function zebraVersionStatus(
  v: { id: string; status: 'draft' | 'approved' },
  t: Pick<ZebraTemplateVersions, 'liveVersion' | 'nextVersion'>,
): { label: string; tone: ChipTone } {
  const { liveVersion: live, nextVersion: next } = t
  if (live && v.id === live.id) return { label: 'In use', tone: 'ok' }
  if (next && v.id === next.id) {
    return { label: next.effectiveDate ? `Approved from ${dayText(next.effectiveDate)}` : 'Approved', tone: 'ok' }
  }
  if (v.status === 'draft') return { label: 'Draft', tone: 'warn' }
  return { label: 'Approved', tone: 'off' }
}

/** The Version cell, e.g. `v1`, `v1 · v2 from Oct 20`, `v1 · v2 draft`, `v1 draft`. */
export function zebraVersionText(t: ZebraTemplateVersions): string {
  const parts: string[] = []
  if (t.liveVersion) parts.push(`v${t.liveVersion.version}`)
  if (t.nextVersion) {
    const day = t.nextVersion.effectiveDate
    parts.push(day ? `v${t.nextVersion.version} from ${dayText(day, false)}` : `v${t.nextVersion.version}`)
  }
  if (t.draftVersion) parts.push(`v${t.draftVersion.version} draft`)
  return parts.length > 0 ? parts.join(' · ') : '—'
}

/** One placeholder in the live check under the layout. */
export interface ZebraCheckRow {
  name: string
  known: boolean
  note: string
}

export interface ZebraBodyCheck {
  /** Each well-formed `{{name}}` in the layout, once, in order of first use. */
  placeholders: string[]
  /** Every problem, in the order shown; saving waits until there are none. */
  problems: string[]
  rows: ZebraCheckRow[]
}

// The only accepted form: lowercase letters, digits and _ between double
// braces, with no spaces.
const PLACEHOLDER_TOKEN = /\{\{([a-z0-9_]+)\}\}/g
// What is left once the good placeholders are taken out: a whole `{{...}}` on
// one line, else a lone `{{` with the word after it or a lone `}}` with the
// word before it.
const MALFORMED_TOKEN = /\{\{[^{}\n]*\}\}|\{\{[^{}\s]*|[^{}\s]*\}\}/g
// `^FD` text, optionally after the `>:` barcode subset switch, that is only a
// field name: the legacy system filled these by string match.
const BARE_FIELD = /\^FD(>:)?([A-Za-z_]+)\^FS/g

export function unknownPlaceholderMessage(name: string): string {
  return `The backend cannot fill {{${name}}}. It needs a backend change before any template can use it.`
}

export function malformedPlaceholderMessage(text: string): string {
  return `Write each placeholder as {{name}}, with lowercase letters, digits or _ and no spaces: ${text}.`
}

export function bareFieldMessage(text: string, name: string): string {
  return `${text} is a bare legacy field name and would print as text. Use {{${name}}}.`
}

/** A name in legacy form: no underscores, in capitals (`lot_num` and `LOT_NUM` are both `LOTNUM`). */
const legacyName = (name: string) => name.replace(/_/g, '').toUpperCase()

/**
 * The live check on a Zebra layout, the same rules the server applies on save:
 * every placeholder is written `{{name}}` and is one the backend can fill, the
 * layout is one ZPL job framed by ^XA and ^XZ, no legacy field name is left
 * bare in a field, and the layout has a length limit. A layout with no
 * placeholders is allowed: every value on that label is fixed text.
 */
export function checkZebraBody(
  body: string,
  registry: { name: string; filledFrom: string }[],
): ZebraBodyCheck {
  const filledFrom = new Map(registry.map(p => [p.name, p.filledFrom]))
  const problems: string[] = []
  const add = (message: string) => {
    if (!problems.includes(message)) problems.push(message)
  }

  const placeholders: string[] = []
  for (const m of body.matchAll(PLACEHOLDER_TOKEN)) {
    if (!placeholders.includes(m[1])) placeholders.push(m[1])
  }
  const rows: ZebraCheckRow[] = placeholders.map(name => {
    const from = filledFrom.get(name)
    return from === undefined
      ? { name, known: false, note: unknownPlaceholderMessage(name) }
      : { name, known: true, note: `Filled from: ${from}` }
  })
  rows.filter(r => !r.known).forEach(r => add(r.note))

  // A space stands in for each good placeholder, so the braces either side of
  // one cannot join up into a new pair.
  for (const m of body.replace(PLACEHOLDER_TOKEN, ' ').matchAll(MALFORMED_TOKEN)) {
    add(malformedPlaceholderMessage(m[0]))
  }

  const trimmed = body.trim()
  if (!trimmed.startsWith('^XA') || !trimmed.endsWith('^XZ')) add(MESSAGES.zplFraming)

  const byLegacy = new Map(registry.map(p => [legacyName(p.name), p.name]))
  for (const m of body.matchAll(BARE_FIELD)) {
    const name = byLegacy.get(legacyName(m[2]))
    if (name !== undefined) add(bareFieldMessage(m[0], name))
  }

  if (body.length > ZEBRA_BODY_MAX) add(MESSAGES.layoutTooLong)

  return { placeholders, problems, rows }
}

/** The check's summary chip. */
export function zebraCheckSummary(check: ZebraBodyCheck): { label: string; tone: ChipTone } {
  const n = check.problems.length
  if (n === 0) return { label: 'All placeholders can be filled', tone: 'ok' }
  return { label: `${n} ${n === 1 ? 'problem' : 'problems'}`, tone: 'warn' }
}

// ─── Zebra preview ────────────────────────────────────────────────────────────

/**
 * The layout as the preview draws it: each `{{name}}` the backend can fill
 * becomes its made-up sample. Any other `{{name}}` stays as written, so the
 * preview shows exactly which blank is wrong.
 */
export function zplPreviewBody(body: string, placeholders: { name: string; sample: string }[]): string {
  const sample = new Map(placeholders.map(p => [p.name, p.sample]))
  return body.replace(PLACEHOLDER_TOKEN, (token, name: string) => sample.get(name) ?? token)
}

// ─── Printing a real label ────────────────────────────────────────────────────

/** Placeholders printed as barcodes: no `^FH`, so their values go in as is. */
const BARCODE_PLACEHOLDERS = new Set(['supply_bcid', 'equipment_id_bc'])

/**
 * ZPL hex escapes for a text field under `^FH\`, the same as the backend
 * applies before it sends the job: `\` first, then `^` and `~`.
 */
export function zplEscape(value: string): string {
  return value.replace(/\\/g, '\\5C').replace(/\^/g, '\\5E').replace(/~/g, '\\7E')
}

/**
 * A label's real values in the shape `ZplPreview` fills in, escaped exactly as
 * the printed job is, so the preview draws what the printer gets.
 */
export function zplLabelValues(values: Record<string, string>): { name: string; sample: string }[] {
  return Object.entries(values).map(([name, value]) => ({
    name,
    sample: BARCODE_PLACEHOLDERS.has(name) ? value : zplEscape(value),
  }))
}

/** The main button of the print popup. */
export function printButtonText(count: number, signatureRequired: boolean): string {
  const what = count === 1 ? 'label' : `${count} labels`
  return signatureRequired ? `Sign and print ${what}` : `Print ${what}`
}

export interface PrintSummary {
  tone: 'ok' | 'error'
  title: string
  detail: string
}

/**
 * One line on how a print went. "Sent" is the honest word: the print service
 * took the job, which is not proof a label came out.
 */
export function printSummary(
  printerName: string,
  results: {
    outcome: 'accepted' | 'unavailable' | 'rejected' | 'timeout' | 'failed' | 'skipped'
    message: string | null
  }[],
): PrintSummary {
  const sent = results.filter(r => r.outcome === 'accepted').length
  const failed = results.find(r => r.outcome !== 'accepted' && r.outcome !== 'skipped')
  const label = (n: number) => (n === 1 ? '1 label' : `${n} labels`)
  if (!failed) {
    return {
      tone: 'ok',
      title: `${label(sent)} sent to ${printerName}`,
      detail: 'Sent means the print service took the job. Check that the label came out.',
    }
  }
  const notSent = results.length - sent
  // No answer in time: the job may have arrived, so "not sent" would be a guess.
  if (failed.outcome === 'timeout') {
    return {
      tone: 'error',
      title: sent > 0 ? `${label(sent)} sent, then no answer` : 'No answer from the print service',
      detail: failed.message ?? 'Check the printer before printing again.',
    }
  }
  return {
    tone: 'error',
    title: sent > 0 ? `${label(sent)} sent, ${label(notSent)} not sent` : 'Nothing was printed',
    detail: failed.message ?? 'The print service could not take the label.',
  }
}

const LABEL_STOCK = /^\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*in\s*$/i
const RESOLUTION = /^\s*(\d+)\s*dpi\s*$/i

/**
 * The label size and print density for the preview, from the server's own
 * wording: `3.9 x 1.5 in` and `203 dpi` give 3.9 by 1.5 inches at 8 dots per
 * millimetre (300 dpi is 12). Null when either cannot be read.
 */
export function labelSizeForPreview(
  labelStock: string,
  resolution: string,
): { widthIn: number; heightIn: number; dpmm: number } | null {
  const stock = LABEL_STOCK.exec(labelStock)
  const dpi = RESOLUTION.exec(resolution)
  if (!stock || !dpi) return null
  const widthIn = Number(stock[1])
  const heightIn = Number(stock[2])
  const dpmm = Math.round(Number(dpi[1]) / 25.4)
  if (widthIn <= 0 || heightIn <= 0 || dpmm <= 0) return null
  return { widthIn, heightIn, dpmm }
}

/**
 * Whether the label prints upside down on the roll: the last print
 * orientation command in the layout is `^POI` (`^PON` is the normal way up).
 */
export function zplPrintsUpsideDown(body: string): boolean {
  const orientations = [...body.matchAll(/\^PO([NI])/g)]
  return orientations.length > 0 && orientations[orientations.length - 1][1] === 'I'
}

export interface ZebraTemplateDraft {
  name: string
  labelStock: string
  resolution: string
  body: string
  /** `YYYY-MM-DD` from the date input; '' until picked. */
  effectiveFrom: string
  reason: string
}

export type ZebraTemplateErrors = Partial<Record<'name' | 'effectiveFrom' | 'reason', string>>

/** The form's own fields. The layout is checked by `checkZebraBody`. */
export function validateZebraTemplate(d: ZebraTemplateDraft, mode: 'new' | 'edit'): ZebraTemplateErrors {
  const e: ZebraTemplateErrors = {}
  if (!d.name.trim()) e.name = MESSAGES.templateName
  if (!d.effectiveFrom) e.effectiveFrom = MESSAGES.effectiveFrom
  if (!d.reason.trim()) e.reason = mode === 'new' ? MESSAGES.reasonNew : MESSAGES.reasonEdit
  return e
}

// ─── Label types ──────────────────────────────────────────────────────────────

export const LABEL_TYPES = ['equipment', 'product', 'material', 'other'] as const
export type LabelType = (typeof LABEL_TYPES)[number]

export const PRINTER_FAMILIES = ['isbt', 'zebra'] as const
export type PrinterFamily = (typeof PRINTER_FAMILIES)[number]

/** The Printer association table, one row per label type, in the order shown. */
export const LABEL_TYPE_ROWS: { labelType: LabelType; label: string; button: string }[] = [
  { labelType: 'equipment', label: 'Equipment', button: 'Asset tag on an equipment record' },
  { labelType: 'product', label: 'Product', button: 'Product label in a procedure’s products' },
  { labelType: 'material', label: 'Material', button: 'Lot and material labels in inventory' },
  { labelType: 'other', label: 'Other', button: 'Any other label' },
]

/** The short name, e.g. in `3 active Zebra printers`. */
const PRINTER_FAMILY_NAME: Record<PrinterFamily, string> = {
  isbt: 'ISBT',
  zebra: 'Zebra',
}

/** The printer type as the switch above the lists names it. */
export function printerFamilyLabel(family: PrinterFamily): string {
  return family === 'isbt' ? 'ISBT · HemaTrax' : 'Zebra · ZPL'
}

/** The Printers cell; `warn` when no active printer of that type exists yet. */
export function activePrintersText(count: number, family: PrinterFamily): { text: string; warn: boolean } {
  const name = PRINTER_FAMILY_NAME[family]
  if (count <= 0) return { text: `No active ${name} printer yet`, warn: true }
  return { text: `${count} active ${name} ${count === 1 ? 'printer' : 'printers'}`, warn: false }
}

/** The notice after a change, e.g. `Equipment labels now go to Zebra printers.` */
export function labelTypeSavedText(labelType: LabelType, family: PrinterFamily): string {
  const label = LABEL_TYPE_ROWS.find(r => r.labelType === labelType)?.label ?? labelType
  return `${label} labels now go to ${PRINTER_FAMILY_NAME[family]} printers.`
}

/** One label size a printer type offers; `ready` when an approved template uses it. */
export interface LabelConfiguration {
  text: string
  ready: boolean
}

/** Width x height from e.g. `4 x 4` or `3.9 x 1.5 in`, for ordering largest first. */
function sizeArea(size: string): number {
  const m = /^\s*([\d.]+)\s*x\s*([\d.]+)/i.exec(size)
  return m ? Number(m[1]) * Number(m[2]) : 0
}

/**
 * The Configurations cell: every label size the printer type offers through
 * a template that is not retired, largest first. ISBT sizes come from the
 * templates' label size (a template still "To confirm" has none); Zebra ones
 * are size and resolution, because a Zebra layout fits only that pair. A size
 * is `ready` once at least one approved template uses it.
 */
export function availableConfigurations(
  family: PrinterFamily,
  isbt: { labelSize: string | null; active: boolean; approvedAt: string | null }[],
  zebra: { labelStock: string; resolution: string; status: 'draft' | 'approved' | 'scheduled' | 'retired' }[],
): LabelConfiguration[] {
  const found = new Map<string, LabelConfiguration & { area: number }>()
  const add = (text: string, ready: boolean, area: number) => {
    const seen = found.get(text)
    if (seen) seen.ready = seen.ready || ready
    else found.set(text, { text, ready, area })
  }
  if (family === 'isbt') {
    for (const t of isbt) if (t.active && t.labelSize) add(t.labelSize, t.approvedAt !== null, sizeArea(t.labelSize))
  } else {
    for (const t of zebra) {
      if (t.status !== 'retired') add(`${t.labelStock} at ${t.resolution}`, t.status === 'approved', sizeArea(t.labelStock))
    }
  }
  return [...found.values()]
    .sort((a, b) => b.area - a.area || a.text.localeCompare(b.text))
    .map(({ text, ready }) => ({ text, ready }))
}

// ─── Department / location ────────────────────────────────────────────────────

/**
 * Free text with suggestions: the values already used in this tenant, each
 * once (ignoring case and outer spaces), in alphabetical order.
 */
export function locationSuggestions(...sources: (string | null | undefined)[][]): string[] {
  const seen = new Map<string, string>()
  for (const list of sources) {
    for (const raw of list) {
      const value = raw?.trim()
      if (!value) continue
      const key = value.toLowerCase()
      if (!seen.has(key)) seen.set(key, value)
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b))
}

// ─── Form checks ──────────────────────────────────────────────────────────────

export interface IsbtTemplateDraft {
  displayName: string
  fileName: string
  /** null is "To confirm". */
  labelSize: string | null
  use: IsbtUse
  dateFormat: IsbtDateFormat
  active: boolean
}

export type IsbtTemplateErrors = Partial<Record<'displayName' | 'fileName', string>>

export function validateIsbtTemplate(d: IsbtTemplateDraft): IsbtTemplateErrors {
  const e: IsbtTemplateErrors = {}
  if (!d.displayName.trim()) e.displayName = MESSAGES.displayName
  const file = fileNameError(d.fileName)
  if (file) e.fileName = file
  return e
}

export interface IsbtPrinterDraft {
  displayName: string
  queueName: string
  loadedStock: string
  allowedTemplateIds: string[]
  location: string
  signatureRequired: boolean
  active: boolean
}

export type IsbtPrinterErrors = Partial<Record<'displayName' | 'queueName' | 'allowed', string>>

export function validateIsbtPrinter(d: IsbtPrinterDraft, choices: AllowedChoice[]): IsbtPrinterErrors {
  const e: IsbtPrinterErrors = {}
  if (!d.displayName.trim()) e.displayName = MESSAGES.displayName
  const queue = queueNameError(d.queueName)
  if (queue) e.queueName = queue
  // Only an active printer needs a template: one whose templates are all
  // retired can still be retired itself.
  if (d.active && !hasOpenPick(d.allowedTemplateIds, choices)) e.allowed = MESSAGES.pickTemplate
  return e
}

export interface ZebraPrinterDraft {
  displayName: string
  connection: ZebraConnection
  host: string
  port: string
  serverQueueName: string
  resolution: string
  loadedStock: string
  allowedTemplateIds: string[]
  location: string
  signatureRequired: boolean
  active: boolean
}

export type ZebraPrinterErrors = Partial<
  Record<'displayName' | 'host' | 'port' | 'serverQueueName' | 'allowed', string>
>

export function validateZebraPrinter(d: ZebraPrinterDraft, choices: AllowedChoice[]): ZebraPrinterErrors {
  const e: ZebraPrinterErrors = {}
  if (!d.displayName.trim()) e.displayName = MESSAGES.displayName
  if (!d.host.trim()) e.host = MESSAGES.host
  const port = portError(d.port)
  if (port) e.port = port
  if (d.connection === 'print_server' && !d.serverQueueName.trim()) e.serverQueueName = MESSAGES.serverQueue
  // As for ISBT: a retired printer needs no template.
  if (d.active && !hasOpenPick(d.allowedTemplateIds, choices)) e.allowed = MESSAGES.pickTemplate
  return e
}
